import { useMemo } from "react";

import { faNoteMedical } from "@fortawesome/pro-regular-svg-icons";
import Box from "@mui/material/Box";
import { useGridApiRef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import type { CareEventBilling, Status } from "api/schemas/CareEventBilling";
import type { Dialog } from "routes/admin-billing/hooks/useBillingState";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay";
import { GridToolbar } from "shared/molecules/GridToolbar";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { useWindowSize } from "utils/hooks";

import { createColumns } from "./columns";

interface Props {
  bulkActions?: React.ReactNode;
  data: CareEventBilling[];
  filter: Status;
  isLoadingMedicalNote: boolean;
  selectedCareEvent: CareEventBilling | null;
  selectedCareEventIDs: number[];
  onSelectCareEvent: (careEvent: CareEventBilling) => void;
  onSelectCareEventIDs: (ids: number[]) => void;
  onShowDialog: (value: Dialog) => void;
}

export function BillingTable({
  bulkActions,
  data,
  filter,
  isLoadingMedicalNote,
  selectedCareEvent,
  selectedCareEventIDs,
  onSelectCareEvent,
  onSelectCareEventIDs,
  onShowDialog,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const apiRef = useGridApiRef();

  const { isSmallScreen } = useWindowSize();
  const smallScreenProps = useMemo(() => (isSmallScreen ? { minWidth: 120 } : {}), [isSmallScreen]);

  const colors = useMemo(
    () => ({
      black: theme.colors.black,
      green: theme.colors.redesign.g100,
      red: theme.colors.redesign.r100,
    }),
    [theme.colors.black, theme.colors.redesign.g100, theme.colors.redesign.r100]
  );

  const columns = useMemo(
    () =>
      createColumns({
        filter,
        isLoadingMedicalNote,
        selectedCareEvent,
        smallScreenProps,
        t,
        colors,
        onSelectCareEvent,
        onSelectCareEventIDs,
        onShowDialog,
      }),
    [
      filter,
      isLoadingMedicalNote,
      selectedCareEvent,
      smallScreenProps,
      t,
      colors,
      onSelectCareEvent,
      onSelectCareEventIDs,
      onShowDialog,
    ]
  );

  const getstatusTranslation = (status: Status) => {
    switch (status) {
      case "pending":
        return t("admin_billing.buttons.pending");
      case "completed":
        return t("admin_billing.buttons.completed");
      case "discarded":
        return t("admin_billing.buttons.discarded");
      default:
        return "";
    }
  };

  const statusTranslation = getstatusTranslation(filter).toLocaleLowerCase();
  const csvFilename = `${t("views.billing")}_${statusTranslation}_${new Date().toISOString()}`;

  return (
    <Box sx={{ width: "100%" }}>
      <GridToolbar
        apiRef={apiRef}
        bulkActions={bulkActions}
        csvFilename={csvFilename}
        count={data.length}
        itemLabel={t("admin_billing.notes")}
        onOpenColumns={() => {
          // @ts-expect-error depends on MUI X version
          apiRef.current.showPreferences?.("columns");
        }}
      />
      <MUIDataGrid
        apiRef={apiRef}
        rows={data}
        columns={columns}
        getRowId={row => row.care_event_id}
        checkboxSelection={filter === "pending"}
        disableRowSelectionOnClick
        rowSelectionModel={selectedCareEventIDs}
        onRowSelectionModelChange={rowSelectionModel => {
          if (filter === "pending") onSelectCareEventIDs(rowSelectionModel as number[]);
        }}
        slots={{
          noRowsOverlay: () => <EmptyResultOverlay text={t("admin_billing.no_rows")} icon={faNoteMedical} />,
          noResultsOverlay: () => <EmptyResultOverlay />,
        }}
        localeText={{
          noRowsLabel: "",
          noResultsOverlayLabel: "",
        }}
        hideHeader
        transparentBackground
      />
    </Box>
  );
}
